import { useState, useEffect } from 'react';
import { APIClient } from './APIClient';
import { API_URL } from '../settings/local_settings';
import { LocalStorage } from './LocalStorage';

/**
 * El cupo del Portal del Cliente de la agencia, leído en UN solo lugar.
 *
 * Dos preguntas distintas que no hay que confundir:
 * - `hasPortal`: el plan incluye el Portal (`max_client_users != 0`). Decide si el portal SE VE
 *   (pestañas, entradas de menú, módulos, asignar contenido). Es lo que devuelven
 *   `usePortalSubscriptionGating` (`canAssignToPortal`) y `fetchPortalSubscriptionGating`.
 * - `canAddClientUser`: queda lugar para otro usuario cliente. Decide SÓLO si se puede invitar.
 *   Con el cupo lleno se muestra el límite con el CTA «Mejorar plan» (`ClientUsersLimitNotice`).
 *
 * Hasta el 2026-09-25 todo leía `can_add_client_user`, así que al llenar el cupo el portal
 * desaparecía justo cuando la agencia necesitaba ver el límite (card U5Yuxys2).
 *
 * Source of truth: `GET /get-logged-user-data/` → `client_portal {enabled, max, current, can_add}`.
 * Un backend anterior a ese campo manda sólo `can_add_client_user`: ahí se cae al criterio viejo.
 *
 * ⚠️ **El cache va keyeado por token, igual que el de `APIClient`.** El logout es SPA
 * (`removeUserData()` + `<Navigate to="/login"/>`, sin reload) y no limpia este módulo: sin la key,
 * un logout seguido de un login con OTRA cuenta en la misma pestaña le sirve al usuario nuevo el
 * gate del anterior — y desde que el chrome consume este hook, ese gate decide qué entradas de
 * menú ve. Es la misma clase de fuga que `APIClient` evita poniendo el token en su key.
 *
 * El fetch va con `{cache: true}` para compartir el request en vuelo con `UserModulesContext`, que
 * pide el MISMO endpoint en el mismo tick del arranque. Sin eso, montar este hook en el chrome
 * volvía a hacer dos llamadas a un endpoint que el repo ya había colapsado de 3 a 1.
 *
 * Refetch manual: `refetchPortalSubscriptionGating()` después de un upgrade o de un rechazo por
 * límite (el cupo cambió entre la carga y la acción).
 */

const NO_PORTAL = Object.freeze({ hasPortal: false, canAddClientUser: false, current: 0, max: 0 });

// Lo que ve una pantalla cuando el cupo NO se pudo leer (red, 5xx). Para la VISIBILIDAD es
// fail-closed (el portal no se muestra), pero no es un dato: `error` lo distingue de un plan sin
// portal, así nadie pinta «tu plan no incluye el Portal» por un blip, y NO se cachea ni se
// difunde (la próxima pantalla que monte vuelve a preguntar).
const UNKNOWN = Object.freeze({ ...NO_PORTAL, max: null, error: true });

let cachedToken = null;
let cachedQuota = null;
let inFlight = null;
let inFlightToken = null;
// Cada pedido lleva un número: sólo el más nuevo puede escribir el cache. Sin esto, un `fetchOnce`
// que resuelve DESPUÉS de un `refetch` pisaba el valor fresco con el viejo.
let requestSeq = 0;
const listeners = new Set();

function currentToken() {
    return LocalStorage.fetchUserToken() || null;
}

/**
 * El cupo a partir del payload de `get-logged-user-data/`, o `null` si la respuesta es un error
 * (`status !== 'OK'`): un error no es un plan sin portal. Exportado para los tests.
 */
export function quotaFromUserData(data) {
    if (!data || data.status !== 'OK') return null;
    const portal = data.client_portal;
    if (portal) {
        return {
            hasPortal: portal.enabled === true,
            canAddClientUser: portal.can_add === true,
            current: portal.current || 0,
            max: portal.max === undefined ? null : portal.max,
        };
    }
    const canAdd = data.can_add_client_user !== false;
    return { hasPortal: canAdd, canAddClientUser: canAdd, current: null, max: null };
}

/** El valor cacheado, o `null` si no hay o si es de otra sesión. */
function cachedValue() {
    if (cachedQuota === null) return null;
    return cachedToken === currentToken() ? cachedQuota : null;
}

function notifyListeners(quota) {
    listeners.forEach((listener) => listener(quota));
}

/**
 * Pide el cupo y decide qué hacer con la respuesta:
 * - dato → se cachea y se notifica a todas las pantallas montadas;
 * - error en la PRIMERA carga (no hay nada cacheado) → se notifica `UNKNOWN` para que nadie quede
 *   cargando, sin cachearlo;
 * - error en un REFRESCO → se ignora: lo que cada pantalla ya tenía sigue valiendo;
 * - respuesta vieja (hubo un pedido más nuevo) o de OTRA sesión (logout + login en la misma
 *   pestaña) → se descarta sin escribir nada.
 * Resuelve con el cupo que quedó vigente, o `UNKNOWN`.
 */
function request({ useApiCache }) {
    const seq = ++requestSeq;
    const token = currentToken();
    const promise = new APIClient(API_URL + 'get-logged-user-data/').call('GET', {}, { cache: useApiCache })
        .catch(() => null)
        .then((data) => {
            const stale = seq !== requestSeq || token !== currentToken();
            if (stale) return cachedValue() || UNKNOWN;
            const quota = quotaFromUserData(data);
            if (quota) {
                cachedToken = token;
                cachedQuota = quota;
                notifyListeners(quota);
                return quota;
            }
            const previous = cachedValue();
            if (previous) return previous;
            notifyListeners(UNKNOWN);
            return UNKNOWN;
        })
        .finally(() => {
            if (inFlight === promise) {
                inFlight = null;
                inFlightToken = null;
            }
        });
    inFlight = promise;
    inFlightToken = token;
    return promise;
}

function fetchOnce() {
    // El pedido en vuelo sólo se comparte si es de ESTA sesión.
    if (inFlight && inFlightToken === currentToken()) return inFlight;
    return request({ useApiCache: true });
}

/** El cupo completo, para componentes de clase: `{hasPortal, canAddClientUser, current, max, error?}`. */
export function fetchClientPortalQuota() {
    const cached = cachedValue();
    return cached === null ? fetchOnce() : Promise.resolve(cached);
}

/** El cupo cacheado, sin pedir nada (`null` si no hay). Para inicializar un state sin parpadeo. */
export function peekClientPortalQuota() {
    return cachedValue();
}

/** ¿El plan incluye el Portal? Para componentes de clase. Promesa con el booleano. */
export function fetchPortalSubscriptionGating() {
    return fetchClientPortalQuota().then((quota) => quota.hasPortal);
}

/**
 * Vuelve a pedir el cupo, salteando el cache de 30s de `APIClient` (después de quitar un usuario o
 * de un rechazo por límite). Si falla, conserva lo que había: un blip no puede convertirse en
 * «el plan no incluye el Portal» para toda la sesión.
 */
export function refetchPortalSubscriptionGating() {
    return request({ useApiCache: false });
}

/** El cupo completo: `{loading, hasPortal, canAddClientUser, current, max}`. */
export function useClientPortalQuota() {
    const [state, setState] = useState(() => {
        const cached = cachedValue();
        return { loading: cached === null, ...(cached || NO_PORTAL) };
    });

    // El token se lee en CADA render: un login/logout dentro de la misma pestaña no remonta este
    // hook, así que sin esto el efecto no se volvería a disparar y el gate quedaría pegado.
    const token = currentToken();

    useEffect(() => {
        const listener = (quota) => setState({ loading: false, ...quota });
        // Se escucha SIEMPRE, no sólo mientras carga: así un `refetch` tras un rechazo por límite
        // le llega también a las pantallas que ya tenían el valor.
        listeners.add(listener);
        const cached = cachedValue();
        if (cached !== null) {
            setState({ loading: false, ...cached });
        } else {
            setState({ loading: true, ...NO_PORTAL });
            fetchOnce();
        }
        return () => { listeners.delete(listener); };
    }, [token]);

    return state;
}

/** Sólo para tests: el cache es de módulo y sobrevive entre casos. */
export function __resetClientPortalQuotaCache() {
    cachedQuota = null;
    cachedToken = null;
    inFlight = null;
    inFlightToken = null;
    listeners.clear();
}

/** ¿El plan incluye el Portal? `canAssignToPortal` = `hasPortal` (nombre histórico). */
export function usePortalSubscriptionGating() {
    const { loading, hasPortal } = useClientPortalQuota();
    return { loading, canAssignToPortal: hasPortal };
}
